import React, { useState, useContext } from "react"
import { Link } from "react-router-dom"
import { ChevronLeft } from "react-bootstrap-icons"
import Header from "../../components/shared/Header/Header"
import Footer from "../../components/shared/Footer/Footer"
import { FeedSpinner } from "../../components/shared/Spinner"
import { RequestError } from "../../components/shared/RequestError"
import { UserContext } from "../../components/HomeApp"
import { Currency } from "../../util/numbers"
import { SmartDateFormat, EventDateFormat, DateFormat } from "../../util/dates"
import {
  usePayout,
  useAddPayoutDistribution,
  useUpdatePayoutDistribution,
  useRemovePayoutDistribution,
  useSendAllPayouts,
} from "../../hooks/usePayout"
import { usePayoutTicketPurchases } from "../../hooks/usePayoutTicketPurchases"
import { usePost } from "../../hooks/usePost"
import style from "../../components/shared/PageStyle.module.css"
import { PayeeSearch, EditDistributionForm } from "./PayeeSearch"
import { Dialog } from "@reach/dialog"
import "@reach/dialog/styles.css"
import ShowSummary from "./ShowSummary"
import CashTicketsSummary from "./CashTicketsSummary"
import PerformersInterestTable from "./PerformersInterestTable"
import DistributionRows from "./DistributionRows"
import DistributionTotalRow from "./DistributionTotalRow"
import OwnerRow from "./OwnerRow"
import { PayoutData, PayoutDistribution, PerformerInterest } from "./types"
import { generateTicketsCSV, downloadCSV } from "../TicketManagementPage/csvExport"
import type { EventInformationPost } from "../TicketManagementPage/EventInformation"
import type { TicketPurchaseWithPurchaser } from "../../requests/ticketPurchases"

const PayoutDistributionView: React.FC<{ payoutId: string }> = ({ payoutId }) => {
  const { user } = useContext(UserContext)
  const { data, status, error } = usePayout(payoutId)
  const payout: PayoutData | null = data?.payout || null
  const performers = (data?.performers ?? []) as PerformerInterest[]

  const { data: ticketPurchasesData } = usePayoutTicketPurchases(Number(payoutId), 1, 500)
  const ticketPurchases = (ticketPurchasesData?.items ?? []) as TicketPurchaseWithPurchaser[]

  // Fetch the full post the same way TicketManagementPage does, so the CSV built here
  // uses identical data (Ticket Price, Max Tickets, etc.) to the one built there.
  const ticketsPostId = payout?.tickets_post?.id
  const { data: fullPostData } = usePost(ticketsPostId, "content_hash,event_ticket_summary", {
    enabled: !!ticketsPostId,
  })
  const fullPost = (fullPostData as { post?: EventInformationPost } | undefined)?.post

  const addDistribution = useAddPayoutDistribution(payoutId)
  const updateDistribution = useUpdatePayoutDistribution(payoutId)
  const removeDistribution = useRemovePayoutDistribution(payoutId)
  const sendAll = useSendAllPayouts(payoutId)

  const [sendError, setSendError] = useState<string | null>(null)
  const [sendSuccess, setSendSuccess] = useState(false)
  const [editingDist, setEditingDist] = useState<PayoutDistribution | null>(null)
  const [showAddModal, setShowAddModal] = useState(false)

  const isReadOnly = payout?.status !== "released"

  const handleAddPayee = async (
    bandId: number | null,
    email: string | null,
    role: string | null,
    amount: number,
  ) => {
    try {
      await addDistribution.mutateAsync({
        band_id: bandId || undefined,
        pending_band_email: email || undefined,
        role: role || undefined,
        amount,
      })
    } catch (e: any) {
      alert(e.message)
    }
  }

  const handleUpdatePayee = async (
    distId: number,
    bandId: number | null,
    email: string | null,
    role: string | null,
    amount: number,
  ) => {
    try {
      await updateDistribution.mutateAsync({
        id: distId,
        band_id: bandId || undefined,
        pending_band_email: email || undefined,
        role: role || undefined,
        amount,
      })
      setEditingDist(null)
    } catch (e: any) {
      alert(e.message)
    }
  }

  const handleRemove = async (distId: number) => {
    try {
      await removeDistribution.mutateAsync(distId)
    } catch (e: any) {
      alert(e.message)
    }
  }

  const handleSendAll = async () => {
    setSendError(null)
    try {
      await sendAll.mutateAsync()
      setSendSuccess(true)
    } catch (e: any) {
      setSendError(e.message)
    }
  }

  const handleDownloadCSV = () => {
    if (!payout) return

    // Use the fully-loaded post (same shape TicketManagementPage uses) when available,
    // so both pages' CSVs are generated from identical data. Fall back to the minimal
    // fields already on hand from the payout itself if that fetch hasn't resolved yet.
    const csvPost: EventInformationPost = fullPost ?? {
      title: payout.tickets_post?.title,
      nada_url: payout.tickets_post?.nada_url ?? undefined,
      start_dt: payout.tickets_post?.start_dt ?? undefined,
    }

    const stats = {
      total: ticketPurchases.length,
      redeemed: ticketPurchases.filter((t) => t.redeemed).length,
      unredeemed: ticketPurchases.filter((t) => !t.redeemed).length,
    }

    const csvContent = generateTicketsCSV({
      post: csvPost,
      tickets: ticketPurchases,
      stats,
      payout,
    })
    const filename = `payout-${payout.tickets_post?.title?.replace(/[^a-z0-9]/gi, "-").toLowerCase() || payout.id}-${new Date().toISOString().split("T")[0]}.csv`
    downloadCSV(csvContent, filename)
  }

  return (
    <div className={style.rootElStyle}>
      <Header user={user} />
      <div className="grid grid-cols-12 w-full pb-2 px-4">
        <div className="col-span-12">
          {status === "loading" ? (
            <FeedSpinner />
          ) : status === "error" ? (
            <RequestError error={error} />
          ) : payout ? (
            <>
              <div className="flex items-center justify-between mt-4">
                <Link
                  to="/dashboard/payouts"
                  className="inline-flex items-center text-sm text-gray-600 hover:text-black"
                >
                  <ChevronLeft size={14} className="mr-1" />
                  Payouts
                </Link>
                <button
                  type="button"
                  onClick={handleDownloadCSV}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 focus:outline-none"
                >
                  Download CSV
                </button>
              </div>
              <h1 className="border-b-4 mt-1 border-black font-bold mb-1 text-xl uppercase">
                Payout{" "}
                {payout.tickets_post?.start_dt
                  ? EventDateFormat(payout.tickets_post.start_dt, payout.tickets_post.timezone)
                  : SmartDateFormat({ dateStr: payout.released_at || "" })}
              </h1>
              {payout.tickets_post?.title && (
                <div className="mb-4">
                  <h2 className="text-lg font-bold">{payout.tickets_post.title}</h2>
                  {payout.tickets_post.nada_url && (
                    <a
                      href={payout.tickets_post.nada_url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-blue-600 text-sm"
                    >
                      View Show
                    </a>
                  )}
                </div>
              )}

              {isReadOnly && (
                <div className="bg-gray-100 border border-gray-300 rounded p-3 mb-4 text-sm text-gray-700">
                  This payout has been sent. It is now read only.
                </div>
              )}

              <ShowSummary payout={payout} />
              <CashTicketsSummary payout={payout} />
              <PerformersInterestTable performers={performers} />

              <div className="mt-6">
                <hr className="border-t-2 border-black mb-4" />
                {!isReadOnly && payout.payout_distributions.length === 0 && (
                  <p className="text-sm text-gray-600 mb-4">
                    Add distributions to payout your performers, sound people, door staff or
                    anyone else who worked the show.
                  </p>
                )}

                <table className="w-full mb-3 table-fixed">
                  <colgroup>
                    <col className="w-[15%] lg:w-[20%]" />
                    <col className="w-[15%] lg:w-[20%]" />
                    <col className="w-[20%] lg:w-[25%]" />
                    <col className="w-[10%] lg:w-[15%]" />
                    <col className="w-[40%] lg:w-[20%]" />
                  </colgroup>
                  <DistributionRows
                    distributions={payout.payout_distributions}
                    isReadOnly={isReadOnly}
                    onEdit={setEditingDist}
                    onRemove={handleRemove}
                  />
                  <DistributionTotalRow distributions={payout.payout_distributions} />
                  {!isReadOnly && (
                    <tbody>
                      <tr>
                        <td colSpan={5} className="py-3">
                          <div className="flex lg:justify-end">
                            <button
                              onClick={() => setShowAddModal(true)}
                              className="w-full lg:w-auto border border-black text-black text-xs font-bold uppercase px-2 py-1 hover:bg-gray-100"
                            >
                              Add Distribution
                            </button>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  )}
                  <OwnerRow
                    userName={user?.name || ""}
                    userEmail={user?.email || ""}
                    remainingBandShare={payout.remaining_band_share}
                  />
                  {!isReadOnly && (
                    <tbody>
                      <tr>
                        <td colSpan={5} className="pt-4">
                          <div className="flex flex-col lg:items-end">
                            {sendError && (
                              <div className="text-red-600 mb-2 text-xs">{sendError}</div>
                            )}
                            {sendSuccess ? (
                              <div className="text-green-700 font-bold text-sm">
                                Payouts sent!
                              </div>
                            ) : (
                              <button
                                onClick={handleSendAll}
                                disabled={sendAll.isLoading}
                                className="w-full lg:w-auto bg-green-700 text-white font-bold uppercase px-4 py-2 hover:bg-green-800 disabled:opacity-50"
                              >
                                {sendAll.isLoading ? "Sending..." : "Finalize Payment"}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  )}
                </table>

                {payout.status === "completed" && (
                  <div className="text-sm font-bold text-gray-700">
                    Sent and Distributed on {DateFormat(payout.updated_at)}
                  </div>
                )}

                <Dialog
                  isOpen={showAddModal}
                  onDismiss={() => setShowAddModal(false)}
                  aria-label="Add distribution"
                  style={{ maxWidth: 560, width: "90%", padding: 0, borderRadius: 2 }}
                >
                  <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                    <h2 className="font-bold uppercase text-sm">Add Distribution</h2>
                    <button
                      onClick={() => setShowAddModal(false)}
                      className="text-gray-400 hover:text-gray-600 focus:outline-none text-xl leading-none"
                    >
                      ×
                    </button>
                  </div>
                  <div className="px-4 py-4">
                    <PayeeSearch
                      payoutId={Number(payoutId)}
                      recommendedPayees={payout.recommended_payees}
                      existingDistributions={payout.payout_distributions}
                      remainingAmount={payout.remaining_band_share}
                      onAdd={async (bandId, email, role, amount) => {
                        await handleAddPayee(bandId, email, role, amount)
                        setShowAddModal(false)
                      }}
                    />
                  </div>
                </Dialog>

                <Dialog
                  isOpen={!!editingDist}
                  onDismiss={() => setEditingDist(null)}
                  aria-label="Edit distribution"
                  style={{ maxWidth: 560, width: "90%", padding: 0, borderRadius: 2 }}
                >
                  <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                    <h2 className="font-bold uppercase text-sm">Edit Distribution</h2>
                    <button
                      onClick={() => setEditingDist(null)}
                      className="text-gray-400 hover:text-gray-600 focus:outline-none text-xl leading-none"
                    >
                      ×
                    </button>
                  </div>
                  <div className="px-4 py-4">
                    {editingDist && (
                      <EditDistributionForm
                        initialValues={{
                          bandId: editingDist.band_id,
                          name: editingDist.band_name,
                          role: editingDist.role || "",
                          amount: String(editingDist.amount),
                        }}
                        remainingAmount={payout.remaining_band_share + editingDist.amount}
                        excludeBandIds={payout.payout_distributions
                          .map((d) => d.band_id)
                          .filter((id): id is number => !!id && id !== editingDist.band_id)}
                        excludeEmails={payout.payout_distributions
                          .map((d) => d.pending_band_email)
                          .filter((e): e is string => !!e && e !== editingDist.pending_band_email)}
                        onSave={async (bandId, email, role, amount) => {
                          await handleUpdatePayee(editingDist.id, bandId, email, role, amount)
                        }}
                        onCancel={() => setEditingDist(null)}
                      />
                    )}
                  </div>
                </Dialog>
              </div>
            </>
          ) : null}
        </div>
      </div>
      <Footer />
    </div>
  )
}

export default PayoutDistributionView
